import React from 'react';
import { evaluateResistanceAlerts } from '../utils/microbiologyTemplateEngine';

export default function ResistanceExpertRulesSection({
  data,
  activeIsolateIdx,
  resistanceMechanisms = [],
  onToggleResistance
}) {
  const currentIsolate = data?.isolates?.[activeIsolateIdx] || null;
  const detectedAlerts = currentIsolate ? evaluateResistanceAlerts(currentIsolate) : [];

  return (
    <div className="wb-stage-view">
      {/* Header */}
      <div className="wb-stage-header">
        <div className="wb-stage-title-wrap">
          <h3 className="wb-stage-title">
            Resistance Mechanisms & Clinical Expert Rules
          </h3>
          <span className="wb-stage-subtitle">
            Evaluating: <strong>{currentIsolate?.organismName || 'Selected Isolate'}</strong> (Isolate #{currentIsolate?.isolateNumber || activeIsolateIdx + 1})
          </span>
        </div>
      </div>

      {/* 1. Triggered Expert Rules Review */}
      <div className="wb-card-clean" style={{ marginBottom: '16px' }}>
        <div className="wb-card-clean-header">
          <div>
            <h4 className="wb-card-clean-title">
              Evaluated Clinical Expert Rules ({detectedAlerts.length} Triggered)
            </h4>
            <span className="wb-hint-text">
              Phenotypic rules evaluate AST measurements against EUCAST / CLSI clinical guidelines.
            </span>
          </div>
        </div>

        {detectedAlerts.length === 0 ? (
          <div className="wb-callout-success">
            <span className="wb-callout-icon">✓</span>
            <div>
              <strong>No High-Risk Resistance Patterns Triggered</strong>
              <p>AST values fall within expected standard wild-type / susceptible distributions.</p>
            </div>
          </div>
        ) : (
          <div className="wb-rules-list">
            {detectedAlerts.map((alert, idx) => (
              <div
                key={idx}
                className={`wb-rule-item ${alert.severity === 'critical' ? 'critical' : 'warning'}`}
              >
                <div className="wb-rule-header-row">
                  <div className="wb-rule-badge-title">
                    <span className="wb-tag-rule-code">{alert.code}</span>
                    <strong className="wb-rule-name">{alert.name}</strong>
                  </div>
                  <span className="wb-tag-severity">{alert.category}</span>
                </div>

                <div className="wb-rule-details-grid">
                  <div className="wb-rule-col">
                    <span className="wb-rule-meta-label">Affected Antimicrobials:</span>
                    <strong className="wb-rule-meta-val" style={{ color: '#dc2626' }}>
                      {alert.code === 'ESBL'
                        ? 'Penicillins, Cephalosporins (1st–4th Gen), Aztreonam'
                        : alert.code === 'CRE'
                        ? 'Carbapenems (Meropenem, Imipenem, Ertapenem)'
                        : alert.code === 'MRSA'
                        ? 'All Beta-lactams (Penicillins, Cephalosporins, Carbapenems)'
                        : alert.code === 'VRE'
                        ? 'Glycopeptides (Vancomycin, Teicoplanin)'
                        : alert.code === 'ICR_DTEST'
                        ? 'Clindamycin, Macrolides, Lincosamides'
                        : 'Colistin, Polymyxin B'}
                    </strong>
                  </div>

                  <div className="wb-rule-col">
                    <span className="wb-rule-meta-label">Rule Evidence & Reason:</span>
                    <span className="wb-rule-meta-val">{alert.evidence}</span>
                  </div>

                  <div className="wb-rule-col" style={{ gridColumn: 'span 2' }}>
                    <span className="wb-rule-meta-label">Guideline Recommendation & Reporting Action:</span>
                    <span className="wb-rule-meta-val" style={{ color: '#0f766e', fontWeight: '600' }}>
                      {alert.recommendedAction}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 2. Master Surveillance Resistance Markers */}
      <div className="wb-card-clean">
        <div className="wb-card-clean-header">
          <div>
            <h4 className="wb-card-clean-title">
              Infection Control Surveillance Markers
            </h4>
            <span className="wb-hint-text">
              Select confirmed epidemiological resistance markers for hospital surveillance reporting.
            </span>
          </div>
        </div>

        <div className="wb-surveillance-grid">
          {resistanceMechanisms.map((mech) => {
            const isChecked = currentIsolate?.resistanceFindings?.some(
              (f) => f.mechanismID === mech.mechanismID
            );
            return (
              <label
                key={mech.mechanismID}
                className={`wb-checkbox-card ${isChecked ? 'checked' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => onToggleResistance(mech.mechanismID)}
                  className="wb-checkbox-input"
                />
                <div className="wb-checkbox-card-content">
                  <div className="wb-checkbox-card-top">
                    <strong>{mech.code}</strong>
                    {mech.infectionControlAlert && (
                      <span className="wb-tag-surveillance-alert">Alert</span>
                    )}
                  </div>
                  <span className="wb-checkbox-card-desc">{mech.name}</span>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}
